import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  Drawer,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { BentoCategoriesContext } from "contexts/BentoCategories";
import { BrandsContext } from "contexts/Brands";
import { Dispatch, SetStateAction, useContext, useState } from "react";
import { SearchParams } from "schemas/dashboard";
import { ALL } from "schemas/forms";
import { routes } from "schemas/routes";

import { isPage } from "utils/general";
import { trackEvent } from "utils/tracking";
import { useSearchBrand } from "utils/useSearchBrand";

import CategoryFilter from "../CategoryFilter";
import DiscoverChips from "./Chips";
import styles from "./styles";

interface Props {
  openFilter: boolean;
  setOpenFilter: Dispatch<SetStateAction<boolean>>;
  filtersList: JSX.Element;
  sortFilter?: JSX.Element;
  brandsLoading?: boolean;
  totalCount?: number | null;
  newCampaignView?: boolean;
}

const DiscoverFilterMobile = ({
  openFilter,
  setOpenFilter,
  filtersList,
  sortFilter,
  totalCount,
  brandsLoading,
  newCampaignView,
}: Props) => {
  const { selectedTags } = useSearchBrand();
  const { selectedMetadataTags } = useContext(BrandsContext);
  const { topLevelCategories } = useContext(BentoCategoriesContext);
  const [openMobileCategoryFilter, setOpenMobileCategoryFilter] =
    useState(false);

  const activeFilterCount =
    (selectedTags?.filter(
      (tag) =>
        tag?.name &&
        tag?.params !== SearchParams.SORT_DISCOVER &&
        tag?.key !== ALL,
    ).length ?? 0) + selectedMetadataTags.length;

  const renderFilterBody = () => {
    return (
      <>
        <Box sx={{ backgroundColor: "secondary.light", py: 1, px: 2 }}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Filters</Typography>
            <IconButton
              onClick={() => setOpenFilter(false)}
              sx={{ width: 20, height: 20, fontSize: 14 }}
            >
              <Box component="i" className="fa-regular fa-xmark" />
            </IconButton>
          </Grid>
        </Box>

        <Box sx={{ px: 0, width: "100%" }}>
          <Grid
            onClick={() => setOpenMobileCategoryFilter((prev) => !prev)}
            container
            justifyContent="space-between"
            alignItems="center"
            sx={styles.mobileList}
          >
            <Box sx={{ fontSize: 16 }}>Categories</Box>{" "}
            <Box component="i" className="fa-regular fa-chevron-down" />
          </Grid>
          {openMobileCategoryFilter && (
            <Grid item xs={12} sx={{ width: "100%", pl: 2 }}>
              <CategoryFilter topLevelCategories={topLevelCategories} />
            </Grid>
          )}

          {filtersList}
          {sortFilter && sortFilter}
          <DiscoverChips
            selectedTags={selectedTags}
            selectedMetadataTags={selectedMetadataTags}
          />
        </Box>

        <DialogActions sx={{ py: 2 }}>
          <Button
            variant="contained"
            size="small"
            disableElevation
            onClick={() => setOpenFilter(false)}
          >
            Done
          </Button>
        </DialogActions>
      </>
    );
  };

  return (
    <>
      <Box sx={{ mt: 0.5 }}>
        <Grid container alignItems="center" justifyContent={"space-between"}>
          <Badge
            badgeContent={activeFilterCount}
            color="primary"
            overlap="rectangular"
            sx={{ "& .MuiBadge-badge": { top: 11, right: 4 } }}
          >
            <Button
              onClick={() => {
                trackEvent("Discover Filters Opened", {
                  "Is Mobile Screen": true,
                });
                setOpenFilter(true);
              }}
              sx={{ textTransform: "none", fontSize: 16 }}
            >
              Filters{" "}
              <Box
                component="i"
                className="fa-thin fa-sliders"
                sx={{ ml: 1 }}
              />
            </Button>
          </Badge>

          {!brandsLoading && totalCount && (
            <Typography variant="caption" color="textSecondary">
              {totalCount} results
            </Typography>
          )}
        </Grid>
      </Box>

      {newCampaignView || isPage(routes.campaignView) ? (
        <Dialog
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          maxWidth={false}
          PaperProps={{
            sx: {
              width: { xs: "calc(100vw - 32px)", sm: 440 },
              m: 2,
              overflowX: "hidden",
            },
          }}
        >
          {renderFilterBody()}
        </Dialog>
      ) : (
        <Drawer
          anchor="left"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          PaperProps={{
            sx: {
              width: { xs: "calc(100vw - 48px)", sm: 440 },
              overflowX: "hidden",
            },
          }}
        >
          {renderFilterBody()}
        </Drawer>
      )}
    </>
  );
};

export default DiscoverFilterMobile;
